import { AxiosError } from 'axios'
import { HttpCode, HttpErrorCode } from 'src/models/api/enums/HttpErrorCode'
import { UserDisplayableApiErrorMessage } from 'src/models/api/enums/UserDisplayableApiErrorMessage'
import {
  GeneralApiError,
  BadRequestError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  TooManyRequestsError,
  ServiceUnavailableError,
  NoUnitsAvailableError,
  UnitUnavailableError,
  NoLockersAvailableError,
  InvalidDomainNameError,
  WorkflowNotActiveError,
  WorkflowNotFoundError,
  InvalidOccupancyDurationError,
  RentalInOvertimeError,
  RentalNotOngoingError,
  CartExpiredError,
  RentalAlreadyHasAuthenticationTypeError,
  InvalidAuthenticationCodeError,
  OrderCancelledError,
  OrderAlreadyPaidError,
  OrderPaymentExpiredError,
  InvalidReservationTokenError,
  InvalidOrderStateError,
  InvalidOrderItemStateError,
  LockerOpeningFailedError,
  NetworkApiError,
  TimeoutApiError,
  CanceledApiError,
  ReservationAlreadyInUse,
  ReservationPickupNotAllowed,
} from '../models/error/errors'
import ApiError from 'src/models/api/interfaces/ApiError'

export default class ErrorHandler {
  static getApiErrorMessage(error: AxiosError<ApiError>) {
    return error.response?.data.detail
  }

  static getApiErrorStatus(error: AxiosError<ApiError>) {
    return error.response?.data.status ?? error.response?.status
  }

  static getApiErrorCode(error: AxiosError<ApiError>) {
    return error.code
  }

  static convert(error: AxiosError<ApiError>): Error {
    return this.getNetworkError(error)
  }

  static getNetworkError(error: AxiosError<ApiError>) {
    switch (this.getApiErrorCode(error)) {
      case HttpCode.ECONNABORTED:
        return new TimeoutApiError()
      case HttpCode.ERR_NETWORK:
        return new NetworkApiError()
      case HttpCode.ERR_CANCELED:
        return new CanceledApiError()
      default:
        return this.getError(error)
    }
  }

  static getError(error: AxiosError<ApiError>) {
    switch (this.getApiErrorMessage(error)) {
      case UserDisplayableApiErrorMessage.NO_UNITS_AVAILABLE:
        return new NoUnitsAvailableError(
          UserDisplayableApiErrorMessage.NO_UNITS_AVAILABLE,
        )

      case UserDisplayableApiErrorMessage.UNIT_UNAVAILABLE:
        return new UnitUnavailableError(
          UserDisplayableApiErrorMessage.UNIT_UNAVAILABLE,
        )

      case UserDisplayableApiErrorMessage.NO_LOCKERS_AVAILABLE:
        return new NoLockersAvailableError(
          UserDisplayableApiErrorMessage.NO_LOCKERS_AVAILABLE,
        )

      case UserDisplayableApiErrorMessage.INVALID_DOMAIN_NAME:
        return new InvalidDomainNameError(
          UserDisplayableApiErrorMessage.INVALID_DOMAIN_NAME,
        )

      case UserDisplayableApiErrorMessage.WORKFLOW_NOT_ACTIVE:
        return new WorkflowNotActiveError(
          UserDisplayableApiErrorMessage.WORKFLOW_NOT_ACTIVE,
        )

      case UserDisplayableApiErrorMessage.WORKFLOW_NOT_FOUND:
        return new WorkflowNotFoundError(
          UserDisplayableApiErrorMessage.WORKFLOW_NOT_FOUND,
        )

      case UserDisplayableApiErrorMessage.INVALID_OCCUPANCY_DURATION:
        return new InvalidOccupancyDurationError(
          UserDisplayableApiErrorMessage.INVALID_OCCUPANCY_DURATION,
        )

      case UserDisplayableApiErrorMessage.RENTAL_IN_OVERTIME:
        return new RentalInOvertimeError(
          UserDisplayableApiErrorMessage.RENTAL_IN_OVERTIME,
        )

      case UserDisplayableApiErrorMessage.RENTAL_NOT_ONGOING:
        return new RentalNotOngoingError(
          UserDisplayableApiErrorMessage.RENTAL_NOT_ONGOING,
        )

      case UserDisplayableApiErrorMessage.CART_EXPIRED:
        return new CartExpiredError(UserDisplayableApiErrorMessage.CART_EXPIRED)

      case UserDisplayableApiErrorMessage.ORDER_CANCELLED:
        return new OrderCancelledError(
          UserDisplayableApiErrorMessage.ORDER_CANCELLED,
        )

      case UserDisplayableApiErrorMessage.ORDER_ALREADY_PAID:
        return new OrderAlreadyPaidError(
          UserDisplayableApiErrorMessage.ORDER_ALREADY_PAID,
        )

      case UserDisplayableApiErrorMessage.ORDER_PAYMENT_EXPIRED:
        return new OrderPaymentExpiredError(
          UserDisplayableApiErrorMessage.ORDER_PAYMENT_EXPIRED,
        )

      case UserDisplayableApiErrorMessage.RENTAL_ALREADY_HAS_AUTHENTICATION_TYPE:
        return new RentalAlreadyHasAuthenticationTypeError(
          UserDisplayableApiErrorMessage.RENTAL_ALREADY_HAS_AUTHENTICATION_TYPE,
        )

      case UserDisplayableApiErrorMessage.INVALID_AUTHENTICATION_CODE:
        return new InvalidAuthenticationCodeError(
          UserDisplayableApiErrorMessage.INVALID_AUTHENTICATION_CODE,
        )

      case UserDisplayableApiErrorMessage.RESERVATION_TOKEN_INVALID:
        return new InvalidReservationTokenError(
          UserDisplayableApiErrorMessage.RESERVATION_TOKEN_INVALID,
        )

      case UserDisplayableApiErrorMessage.RESERVATION_ALREADY_IN_USE:
        return new ReservationAlreadyInUse(
          UserDisplayableApiErrorMessage.RESERVATION_ALREADY_IN_USE,
        )

      case UserDisplayableApiErrorMessage.RESERVATION_PICKUP_NOT_ALLOWED:
        return new ReservationPickupNotAllowed(
          UserDisplayableApiErrorMessage.RESERVATION_PICKUP_NOT_ALLOWED,
        )

      case UserDisplayableApiErrorMessage.ORDER_STATE_INVALID:
        return new InvalidOrderStateError(
          UserDisplayableApiErrorMessage.ORDER_STATE_INVALID,
        )

      case UserDisplayableApiErrorMessage.ORDER_ITEM_STATE_INVALID:
        return new InvalidOrderItemStateError(
          UserDisplayableApiErrorMessage.ORDER_ITEM_STATE_INVALID,
        )

      case UserDisplayableApiErrorMessage.LOCKER_OPENING_FAILED:
        return new LockerOpeningFailedError(
          UserDisplayableApiErrorMessage.LOCKER_OPENING_FAILED,
        )

      default:
        console.error(error)
        return this.getApiErrorNotificationFromStatus(error)
    }
  }

  static getApiErrorNotificationFromStatus(error: AxiosError<ApiError>) {
    switch (this.getApiErrorStatus(error)) {
      case HttpErrorCode.BAD_REQUEST:
        return new BadRequestError()

      case HttpErrorCode.UNAUTHORIZED:
        return new UnauthorizedError()

      case HttpErrorCode.FORBIDDEN:
        return new ForbiddenError()

      case HttpErrorCode.NOT_FOUND:
        return new NotFoundError()

      case HttpErrorCode.CONFLICT:
        return new ConflictError()

      case HttpErrorCode.TOO_MANY_REQUESTS:
        return new TooManyRequestsError()

      case HttpErrorCode.SERVICE_UNAVAILABLE:
        return new ServiceUnavailableError()

      default:
        return new GeneralApiError()
    }
  }
}
